import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { useAuth } from "AuthContext";
import { API_URL } from "config";
import { hasExplicitPermission } from "utils/granularPermissions";

import { ProjectLiveChatContext } from "./ProjectLiveChatContext";
import ProjectLiveChatDialog from "./ProjectLiveChatDialog";
import useProjectLiveChatRealtime from "./useProjectLiveChatRealtime";

const UNREAD_BATCH_SIZE = 100;
const MAX_SEEN_REALTIME_MESSAGES = 1000;

const normalizeProjectId = (value) => {
  const projectId = Number(value);
  return Number.isInteger(projectId) && projectId > 0 ? projectId : null;
};

const normalizeUnreadCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
};

const normalizeProject = (project) => {
  const projectId = normalizeProjectId(project?.id ?? project?.project_id);
  if (!projectId) return null;

  return {
    id: projectId,
    quest_code: project?.quest_code || project?.project_code || String(projectId),
    project_title: project?.project_title || project?.project_name || "",
  };
};

const documentIsVisible = () =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

export default function ProjectLiveChatProvider({ children }) {
  const { isLoggedIn, isAuthReady, user, permissions = [], userPermission = [] } = useAuth();
  const [activeProject, setActiveProject] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [eventRevision, setEventRevision] = useState(0);
  const [projectLiveChatUnreadCounts, setProjectLiveChatUnreadCounts] = useState({});
  const [projectLiveChatUnreadRevision, setProjectLiveChatUnreadRevision] = useState(0);

  const activeProjectRef = useRef(activeProject);
  const minimizedRef = useRef(minimized);
  const watchedProjectIdsRef = useRef(new Map());
  const pendingUnreadSyncIdsRef = useRef(new Set());
  const unreadSyncTimerRef = useRef(null);
  const unreadSyncGenerationRef = useRef(0);
  const unreadRequestControllersRef = useRef(new Set());
  const unreadMutationVersionsRef = useRef(new Map());
  const seenRealtimeMessagesRef = useRef(new Set());
  const seenRealtimeMessageOrderRef = useRef([]);

  activeProjectRef.current = activeProject;
  minimizedRef.current = minimized;

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...(permissions || []), ...(userPermission || [])])),
    [permissions, userPermission]
  );
  const canUseProjectLiveChat = Boolean(
    isAuthReady &&
      isLoggedIn &&
      String(user?.actor_type || "").toLowerCase() === "user" &&
      user?.id &&
      hasExplicitPermission(grantedPermissions, "chat.use")
  );
  const authorizedUserId = canUseProjectLiveChat ? normalizeProjectId(user?.id) || 0 : 0;

  const cancelUnreadSync = useCallback(() => {
    unreadSyncGenerationRef.current += 1;
    if (unreadSyncTimerRef.current !== null) {
      window.clearTimeout(unreadSyncTimerRef.current);
      unreadSyncTimerRef.current = null;
    }
    pendingUnreadSyncIdsRef.current.clear();
    unreadRequestControllersRef.current.forEach((controller) => controller.abort());
    unreadRequestControllersRef.current.clear();
  }, []);

  const updateProjectLiveChatUnreadCount = useCallback((projectIdValue, countValue) => {
    const projectId = normalizeProjectId(projectIdValue);
    if (!projectId) return;

    const key = String(projectId);
    const count = normalizeUnreadCount(countValue);
    unreadMutationVersionsRef.current.set(
      key,
      (unreadMutationVersionsRef.current.get(key) || 0) + 1
    );
    setProjectLiveChatUnreadRevision((revision) => revision + 1);
    setProjectLiveChatUnreadCounts((current) => {
      const currentCount = normalizeUnreadCount(current[key]);
      if (currentCount === count) return current;

      const next = { ...current };
      if (count > 0) {
        next[key] = count;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const incrementProjectLiveChatUnreadCount = useCallback((projectIdValue) => {
    const projectId = normalizeProjectId(projectIdValue);
    if (!projectId) return;

    const key = String(projectId);
    unreadMutationVersionsRef.current.set(
      key,
      (unreadMutationVersionsRef.current.get(key) || 0) + 1
    );
    setProjectLiveChatUnreadRevision((revision) => revision + 1);
    setProjectLiveChatUnreadCounts((current) => ({
      ...current,
      [key]: normalizeUnreadCount(current[key]) + 1,
    }));
  }, []);

  const fetchUnreadCountBatch = useCallback(
    async (projectIds) => {
      if (!canUseProjectLiveChat || !authorizedUserId) return;

      const ids = Array.from(new Set(projectIds.map(normalizeProjectId).filter(Boolean))).slice(
        0,
        UNREAD_BATCH_SIZE
      );
      if (!ids.length) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const generation = unreadSyncGenerationRef.current;
      const mutationVersions = new Map(
        ids.map((projectId) => {
          const key = String(projectId);
          return [key, unreadMutationVersionsRef.current.get(key) || 0];
        })
      );
      const query = new URLSearchParams();
      ids.forEach((projectId) => query.append("project_ids[]", String(projectId)));
      const controller = new AbortController();
      unreadRequestControllersRef.current.add(controller);

      try {
        const response = await fetch(
          API_URL + "/project-chat/unread-counts?" + query.toString(),
          {
            headers: {
              Authorization: "Bearer " + token,
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );
        const payload = await response.json().catch(() => ({}));
        if (
          !response.ok ||
          payload?.success === 0 ||
          controller.signal.aborted ||
          generation !== unreadSyncGenerationRef.current ||
          localStorage.getItem("authToken") !== token
        ) {
          return;
        }

        const responseCounts =
          payload?.unread_counts && typeof payload.unread_counts === "object"
            ? payload.unread_counts
            : {};
        setProjectLiveChatUnreadCounts((current) => {
          let changed = false;
          const next = { ...current };

          ids.forEach((projectId) => {
            const key = String(projectId);
            if (
              (unreadMutationVersionsRef.current.get(key) || 0) !==
              mutationVersions.get(key)
            ) {
              return;
            }

            const count = normalizeUnreadCount(responseCounts[key]);
            const currentCount = normalizeUnreadCount(current[key]);
            if (count === currentCount) return;

            changed = true;
            if (count > 0) {
              next[key] = count;
            } else {
              delete next[key];
            }
          });

          return changed ? next : current;
        });
      } catch {
        // Reverb events continue updating badges if this one-time sync is unavailable.
      } finally {
        unreadRequestControllersRef.current.delete(controller);
      }
    },
    [authorizedUserId, canUseProjectLiveChat]
  );

  const queueUnreadSync = useCallback(
    (projectIds) => {
      if (!canUseProjectLiveChat) return;

      projectIds.forEach((projectIdValue) => {
        const projectId = normalizeProjectId(projectIdValue);
        if (projectId) pendingUnreadSyncIdsRef.current.add(projectId);
      });
      if (!pendingUnreadSyncIdsRef.current.size || unreadSyncTimerRef.current !== null) return;

      unreadSyncTimerRef.current = window.setTimeout(() => {
        unreadSyncTimerRef.current = null;
        const pendingIds = Array.from(pendingUnreadSyncIdsRef.current);
        pendingUnreadSyncIdsRef.current.clear();

        for (let index = 0; index < pendingIds.length; index += UNREAD_BATCH_SIZE) {
          void fetchUnreadCountBatch(pendingIds.slice(index, index + UNREAD_BATCH_SIZE));
        }
      }, 0);
    },
    [canUseProjectLiveChat, fetchUnreadCountBatch]
  );

  const watchProjectLiveChat = useCallback(
    (projectIdValue) => {
      const projectId = normalizeProjectId(projectIdValue);
      if (!canUseProjectLiveChat || !projectId) return () => {};

      const key = String(projectId);
      watchedProjectIdsRef.current.set(key, (watchedProjectIdsRef.current.get(key) || 0) + 1);
      queueUnreadSync([projectId]);

      let watching = true;
      return () => {
        if (!watching) return;
        watching = false;

        const remaining = (watchedProjectIdsRef.current.get(key) || 1) - 1;
        if (remaining > 0) {
          watchedProjectIdsRef.current.set(key, remaining);
        } else {
          watchedProjectIdsRef.current.delete(key);
          pendingUnreadSyncIdsRef.current.delete(projectId);
        }
      };
    },
    [canUseProjectLiveChat, queueUnreadSync]
  );

  const previousAuthorizedUserIdRef = useRef(authorizedUserId);
  useEffect(() => {
    if (previousAuthorizedUserIdRef.current === authorizedUserId) return;

    previousAuthorizedUserIdRef.current = authorizedUserId;
    cancelUnreadSync();
    activeProjectRef.current = null;
    minimizedRef.current = false;
    seenRealtimeMessagesRef.current.clear();
    seenRealtimeMessageOrderRef.current = [];
    unreadMutationVersionsRef.current.clear();
    setActiveProject(null);
    setMinimized(false);
    setProjectLiveChatUnreadCounts({});

    if (authorizedUserId) {
      queueUnreadSync(Array.from(watchedProjectIdsRef.current.keys()));
    }
  }, [authorizedUserId, cancelUnreadSync, queueUnreadSync]);

  useEffect(() => () => cancelUnreadSync(), [cancelUnreadSync]);

  const openProjectLiveChat = useCallback(
    (project) => {
      if (!canUseProjectLiveChat) return false;
      const normalizedProject = normalizeProject(project);
      if (!normalizedProject) return false;

      const sameProject = activeProjectRef.current?.id === normalizedProject.id;
      const wasMinimized = minimizedRef.current;
      activeProjectRef.current = normalizedProject;
      minimizedRef.current = false;
      setActiveProject(normalizedProject);
      setMinimized(false);
      if (sameProject && !wasMinimized) setEventRevision((revision) => revision + 1);
      return true;
    },
    [canUseProjectLiveChat]
  );

  const closeProjectLiveChat = useCallback(() => {
    activeProjectRef.current = null;
    minimizedRef.current = false;
    setActiveProject(null);
    setMinimized(false);
  }, []);

  const minimizeProjectLiveChat = useCallback(() => {
    if (!activeProjectRef.current) return;
    minimizedRef.current = true;
    setMinimized(true);
  }, []);

  const restoreProjectLiveChat = useCallback(() => {
    if (!activeProjectRef.current) return;
    minimizedRef.current = false;
    setMinimized(false);
  }, []);

  const projectIsVisiblyOpen = useCallback(
    (projectId) =>
      activeProjectRef.current?.id === projectId &&
      !minimizedRef.current &&
      documentIsVisible(),
    []
  );

  const rememberRealtimeMessage = useCallback((projectId, messageId) => {
    if (!messageId) return true;

    const key = String(projectId) + ":" + String(messageId);
    if (seenRealtimeMessagesRef.current.has(key)) return false;

    seenRealtimeMessagesRef.current.add(key);
    seenRealtimeMessageOrderRef.current.push(key);
    if (seenRealtimeMessageOrderRef.current.length > MAX_SEEN_REALTIME_MESSAGES) {
      const oldestKey = seenRealtimeMessageOrderRef.current.shift();
      seenRealtimeMessagesRef.current.delete(oldestKey);
    }
    return true;
  }, []);

  const handleRealtimeUpdate = useCallback(
    ({ projectId: projectIdValue, messageId, notify, autoOpen }) => {
      if (!canUseProjectLiveChat) return;

      const projectId = normalizeProjectId(projectIdValue);
      if (!projectId || !rememberRealtimeMessage(projectId, messageId)) return;

      if (autoOpen) {
        const sameProject = activeProjectRef.current?.id === projectId;
        const wasMinimized = minimizedRef.current;
        if (!sameProject) {
          const normalizedProject = normalizeProject({ id: projectId });
          activeProjectRef.current = normalizedProject;
          setActiveProject(normalizedProject);
        }
        minimizedRef.current = false;
        setMinimized(false);
        if (sameProject && !wasMinimized) setEventRevision((revision) => revision + 1);
        return;
      }

      if (notify) {
        if (projectIsVisiblyOpen(projectId)) {
          setEventRevision((revision) => revision + 1);
        } else {
          incrementProjectLiveChatUnreadCount(projectId);
        }
        return;
      }

      if (projectIsVisiblyOpen(projectId)) {
        setEventRevision((revision) => revision + 1);
      }
    },
    [
      canUseProjectLiveChat,
      incrementProjectLiveChatUnreadCount,
      projectIsVisiblyOpen,
      rememberRealtimeMessage,
    ]
  );

  const handleRealtimeSubscribed = useCallback(() => {
    if (!canUseProjectLiveChat) return;

    queueUnreadSync(Array.from(watchedProjectIdsRef.current.keys()));
    const currentProjectId = activeProjectRef.current?.id;
    if (currentProjectId && projectIsVisiblyOpen(currentProjectId)) {
      setEventRevision((revision) => revision + 1);
    }
  }, [canUseProjectLiveChat, projectIsVisiblyOpen, queueUnreadSync]);

  const handleUnreadCountChange = useCallback(
    (projectIdValue, countValue) => {
      let projectId = projectIdValue;
      let count = countValue;

      if (projectIdValue && typeof projectIdValue === "object") {
        projectId = projectIdValue.projectId ?? projectIdValue.project_id;
        count = projectIdValue.unreadCount ?? projectIdValue.unread_count;
      } else if (countValue === undefined) {
        projectId = activeProjectRef.current?.id;
        count = projectIdValue;
      }

      updateProjectLiveChatUnreadCount(projectId, count);
    },
    [updateProjectLiveChatUnreadCount]
  );

  const realtimeStatus = useProjectLiveChatRealtime({
    enabled: canUseProjectLiveChat,
    userId: user?.id,
    onUpdate: handleRealtimeUpdate,
    onSubscribed: handleRealtimeSubscribed,
  });

  const getProjectLiveChatUnreadCount = useCallback(
    (projectIdValue) => {
      const projectId = normalizeProjectId(projectIdValue);
      return projectId ? normalizeUnreadCount(projectLiveChatUnreadCounts[String(projectId)]) : 0;
    },
    [projectLiveChatUnreadCounts]
  );

  const contextValue = useMemo(
    () => ({
      canUseProjectLiveChat,
      openProjectLiveChat,
      closeProjectLiveChat,
      getProjectLiveChatUnreadCount,
      watchProjectLiveChat,
      projectLiveChatUnreadRevision,
    }),
    [
      canUseProjectLiveChat,
      closeProjectLiveChat,
      getProjectLiveChatUnreadCount,
      openProjectLiveChat,
      projectLiveChatUnreadRevision,
      watchProjectLiveChat,
    ]
  );

  return (
    <ProjectLiveChatContext.Provider value={contextValue}>
      {children}
      {canUseProjectLiveChat && (
        <ProjectLiveChatDialog
          open={Boolean(activeProject)}
          project={activeProject}
          minimized={minimized}
          eventRevision={eventRevision}
          realtimeStatus={realtimeStatus}
          onClose={closeProjectLiveChat}
          onMinimize={minimizeProjectLiveChat}
          onRestore={restoreProjectLiveChat}
          onUnreadCountChange={handleUnreadCountChange}
        />
      )}
    </ProjectLiveChatContext.Provider>
  );
}

ProjectLiveChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
